import { useState, useMemo, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { Phone, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Tooltip } from 'react-tooltip';

import type { UrgentCaseDashboardItem } from '@/api/urgent-cases';
import { useGetUrgentCasesDashboard } from '@/hooks/api/use-get-urgent-cases-dashboard';
import { formatId } from '@/utils/strings';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { TrackAcknowledgementSheet } from '@/components/cases/track-acknowledgement-sheet';

import { DashboardModule } from './dashboard-module';
import { FetchStates } from '../fetch-states';

export function UrgentCasesModule() {
  const { t } = useTranslation();
  const { dashboard, mutate, loading } = useGetUrgentCasesDashboard();

  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [selectedCase, setSelectedCase] = useState<UrgentCaseDashboardItem | null>(null);

  const filteredCases = useMemo(() => {
    const cases = dashboard?.cases ?? [];
    return showAcknowledged ? cases : cases.filter((c) => !c.acknowledged_by);
  }, [dashboard?.cases, showAcknowledged]);

  const unacknowledgedCount = dashboard?.unacknowledged_count ?? 0;

  const handleTrackClick = useCallback((urgentCase: UrgentCaseDashboardItem) => {
    setSelectedCase(urgentCase);
  }, []);

  return (
    <>
      <DashboardModule
        title={t('urgent-cases-title')}
        description={t('urgent-cases-description')}
        roles={['vitaziadmin']}
      >
        <div className="flex flex-col h-full">
          {/* Filter Toggle */}
          <div className="flex items-center justify-between pb-3 border-b shrink-0">
            <div className="flex items-center gap-4 py-1">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <span className="font-semibold">
                  {t('urgent-cases-unacknowledged-count', { count: unacknowledgedCount })}
                </span>
              </div>
              <Switch
                checked={showAcknowledged}
                onChange={setShowAcknowledged}
                label={t('urgent-cases-show-acknowledged')}
              />
            </div>
          </div>

          {/* Compact Cases List */}
          <div className="space-y-2 mt-4 overflow-auto flex-1">
            <FetchStates loading={loading}>
              {filteredCases.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {showAcknowledged
                      ? t('urgent-cases-empty-showing-all')
                      : t('urgent-cases-empty-all-acknowledged')}
                  </p>
                </div>
              ) : (
                filteredCases.map((urgentCase) => (
                  <div
                    key={urgentCase.case_id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          data-tooltip-id="sheet-case-id"
                          className="font-mono text-sm font-medium"
                        >
                          {t('urgent-cases-case-prefix', { id: formatId(urgentCase.case_id) })}
                        </span>
                        <Tooltip id="sheet-case-id" place="top">
                          {urgentCase.case_id}
                        </Tooltip>
                        {urgentCase.acknowledged_by ? (
                          <Badge color="green" variant="outline" size="xs" className="gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            {t('cases-table-acknowledged')}
                          </Badge>
                        ) : (
                          <Badge color="red" variant="outline" size="xs" className="gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {t('pending')}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {urgentCase.patient_id}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {urgentCase.clinic_name}
                      </p>
                      {urgentCase.acknowledged_by && urgentCase.acknowledged_at && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          {t('urgent-cases-acknowledged-at', {
                            date: new Date(urgentCase.acknowledged_at).toLocaleString(),
                          })}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTrackClick(urgentCase)}
                        className="gap-2"
                      >
                        <Phone className="h-3 w-3" />
                        {t('urgent-cases-track-button')}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </FetchStates>
          </div>
        </div>
      </DashboardModule>

      {selectedCase && (
        <TrackAcknowledgementSheet
          open={!!selectedCase}
          onOpenChange={(open) => !open && setSelectedCase(null)}
          urgentCase={selectedCase}
          onMutate={() => mutate()}
        />
      )}
    </>
  );
}
